import Box from '@mui/material/Box';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from 'react-i18next';

import { ModalV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';

export type DocPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
};

const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url);

/**
 * Aperçu d'un document (§7) en modale dédiée : image inline ou PDF en iframe, plutôt qu'une simple
 * ouverture dans un nouvel onglet. Conserve un raccourci « Ouvrir dans un onglet » en repli.
 */
export const DocPreviewModal = ({ open, onClose, url, title }: DocPreviewModalProps) => {
  const { t } = useTranslation(['missionDashboard']);
  const resolvedTitle = title ?? t('actionModals.docPreview.defaultTitle', { ns: 'missionDashboard' });
  return (
    <ModalV2
      open={open}
      onClose={onClose}
      title={resolvedTitle}
      icon={<VisibilityRoundedIcon sx={{ fontSize: 17 }} />}
      width={860}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('drawers.common.close', { ns: 'missionDashboard' })}
          </ButtonV2>
          <ButtonV2
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('actionModals.docPreview.openInTab', { ns: 'missionDashboard' })}
          </ButtonV2>
        </>
      }
    >
      <Box
        sx={{
          height: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: dsV2.color.surface2,
          borderRadius: dsV2.radius.sm,
          overflow: 'hidden',
        }}
      >
        {isImageUrl(url) ? (
          <Box
            component="img"
            src={url}
            alt={resolvedTitle}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="iframe"
            src={url}
            title={resolvedTitle}
            sx={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </Box>
    </ModalV2>
  );
};

export default DocPreviewModal;
